/**
 * PauseController class handles the logic for pausing and resuming tasks,
 * managing invigilator authentication, and handling related events.
 * 
 * @class
 * @param {Object} $scope - AngularJS scope object.
 * @param {Object} $rootScope - AngularJS root scope object.
 * @param {Object} $location - AngularJS $location service.
 * @param {Object} $routeParams - AngularJS $routeParams service.
 * @param {Object} TaskSelectionService - Service for selecting tasks.
 * @param {Object} TaskDataService - Service for fetching task data.
 * @param {Object} $bridges - Service for bridge functionalities.
 * @param {Object} $signals - Service for signal handling.
 * @param {Object} $translate - Service for translation.
 * @param {Object} TaskCountdownService - Service for managing task countdown.
 * @param {Object} $interval - AngularJS $interval service.
 * @param {Object} $timeout - AngularJS $timeout service.
 */
export class PauseController {
    constructor($scope, $rootScope, $location, $routeParams, TaskSelectionService, TaskDataService, $bridges, $signals, $translate, TaskCountdownService, $interval, $timeout) {
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$location = $location;
        this.$routeParams = $routeParams;
        this.TaskSelectionService = TaskSelectionService;
        this.TaskDataService = TaskDataService;
        this.$bridges = $bridges;
        this.$signals = $signals;
        this.$translate = $translate;
        this.TaskCountdownService = TaskCountdownService;
        this.$interval = $interval;
        this.$timeout = $timeout;

        this.$logger = $bridges.logger();
        this.$eventLog = $bridges.eventLog();

        this.$scope._logIdentifier = 'PauseController';
        this.$logger.logEnter(this.$scope, 'initialisation');

        this.$scope.invigilatorRequired = TaskCountdownService.getInstance().invigilatorRequired();

        this.taskContext = TaskDataService.fetchContext();
        this.taskUUID = TaskSelectionService.getTaskUUID();
        this.task = this.taskContext.read(this.taskUUID);
        this.redirect = null;

        // Check if the task is not paused and redirect accordingly
        if (!TaskCountdownService.getInstance().isPaused()) {
            if ((angular.isFunction(this.task.isSheet) && this.task.isSheet()) || this.taskUUID !== $routeParams.taskUUID) {
                $location.path(
                    "/sheet/" + $routeParams.taskUUID
                );
            } else {
                $location.path(
                    "/overview/" + this.taskUUID
                );
            }
        }

        // Remove and bind signal handlers for invigilator authentication
        this.$signals.remove("tryInvigilatorAuthenticationFailed");
        this.$signals.on("tryInvigilatorAuthenticationFailed", this._tryInvigilatorAuthenticationFailed.bind(this));

        this.$signals.remove("tryInvigilatorAuthenticationSuccess");
        this.$signals.on("tryInvigilatorAuthenticationSuccess", this._tryInvigilatorAuthenticationSuccess.bind(this));

        // Cleanup signal handlers on scope destroy
        this.$scope.$on('$destroy', () => {
            this.$signals.remove("tryInvigilatorAuthenticationFailed");
            this.$signals.remove("tryInvigilatorAuthenticationSuccess");
        });

        // Bind scope methods
        this.$scope.onUnlockBtn = this.onUnlockBtn.bind(this);
        this.$scope.closeUnlockErrorMessage = this.closeUnlockErrorMessage.bind(this);
        this.$scope.onKeyupPassword = this.onKeyupPassword.bind(this);
        this.$scope.onResume = this.onResume.bind(this);

        this.$logger.logExit(this.$scope, 'initialisation');
    }

    /**
     * Handles the event when invigilator authentication fails.
     * Logs the event, updates the scope, and displays an error message.
     * 
     * @private
     */
    _tryInvigilatorAuthenticationFailed() {
        this.$timeout(() => {
            console.log('_tryInvigilatorAuthenticationFailed');

            this.$scope.invigilatorUnlockCodeInputDisabled = false;
            this._unlockErrorMessage(this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_FAILED'));

            var eMsg = this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_FAILED');
            this.$eventLog.logEvent(eMsg, 'invLoginFailed', '', '', {});
            this.$scope.invigilatorUnlockCode = null;
        });
    }

    /**
     * Handles the event when invigilator authentication succeeds.
     * Logs the event, updates the scope, and resumes the task.
     * 
     * @private
     * @param {string} data - JSON string containing authentication result.
     */
    _tryInvigilatorAuthenticationSuccess(data) {
        this.$timeout(() => {
            var result = JSON.parse(data);
            if (!angular.isObject(result) || result.type !== 'invigilator') {
                this.$logger.warn("Received invalid user data from invigilator login" + JSON.stringify(result));
                this._tryInvigilatorAuthenticationFailed();
            } else if (result.type === 'invigilator') {
                this.$scope.invigilatorIsUnlocked = true;
                this.$signals.remove("tryInvigilatorAuthenticationFailed");
                this.$signals.remove("tryInvigilatorAuthenticationSuccess");

                var eMsg = this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_SUCCESS');
                this.$eventLog.logEvent(eMsg, 'invLoginSuccess', '', '', {});
                this._resumeByInvigilator();

                this.$scope.invigilatorUnlockCodeInputDisabled = false;
            }
        });
    }

    /**
     * Handles the unlock button click event.
     * Attempts invigilator authentication if the unlock code is provided.
     * 
     * @param {string} val - The redirect value.
     */
    onUnlockBtn(val) {
        this.$scope.errorMessage = null;
        if (this.$scope.invigilatorUnlockCode) {
            this.$scope.invigilatorUnlockCodeInputDisabled = true;
            this.$bridges.authentication().tryInvigilatorAuthentication(this.$scope.invigilatorUnlockCode);
            this.redirect = val;
        } else {
            this._unlockErrorMessage(this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_EMPTY'));
        }
    }

    /**
     * Closes the unlock error message if it is displayed.
     */
    closeUnlockErrorMessage() {
        if (this.$scope.unlockHasError) {
            this._unlockErrorMessage(null);
        }
    }

    /**
     * Handles the keyup event on the password input.
     * Closes the unlock error message and triggers unlock if Enter key is pressed.
     * 
     * @param {Object} $event - The keyup event object.
     */
    onKeyupPassword($event) {
        this.closeUnlockErrorMessage();
        if ($event.keyCode === 13) {
            this.onUnlockBtn();
        }
    }

    /**
     * Displays or hides the unlock error message.
     * If a message is provided, it sets the error message and schedules its removal after 5 seconds.
     * 
     * @private
     * @param {string|null} message - The error message to display.
     * @returns {boolean} - Returns false if no message is provided.
     */
    _unlockErrorMessage(message) {
        if (!message) {
            this.$scope.unlockHasError = false;
            this.$scope.unlockErrorMessage = null;
            return false;
        }
        this.$scope.unlockHasError = true;
        this.$scope.unlockErrorMessage = message;

        if (this._removeUnlockHasErrorInterval) this.$interval.cancel(this._removeUnlockHasErrorInterval);
        this._removeUnlockHasErrorInterval = this.$interval(() => {
            this._unlockErrorMessage(null);
        }, 5000);
    }

    /**
     * Resumes the task by the candidate.
     * Logs the event and redirects to the appropriate path.
     * 
     * @param {string} type - The type of resume action.
     */
    onResume(type) {
        this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.EVENTLOG_APPLICATION_RESUME_BY_CANDIDATE"), "resumeByCandidate", '', "resume", {});

        if (this.TaskCountdownService.getInstance().paused) {
            this.TaskCountdownService.getInstance().togglePause();
        }

        if ((angular.isFunction(this.task.isSheet) && this.task.isSheet()) || this.taskUUID !== this.$routeParams.taskUUID) {
            this.$location.path(
                "/sheet/" + this.$routeParams.taskUUID
            );
        } else {
            this.$location.path(
                "/overview/" + this.taskUUID
            );
        }
    }

    /**
     * Resumes the task by the invigilator.
     * Logs the event, toggles the pause state, and redirects to the appropriate path.
     * 
     * @private
     */
    _resumeByInvigilator() {
        var path;

        this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.EVENTLOG_APPLICATION_RESUME_BY_INVIGILATOR"), "resumByInv", '', "resume", {});
        if (this.TaskCountdownService.getInstance().paused) {
            this.TaskCountdownService.getInstance().togglePause();
        }

        if (this.redirect === 'invigilatorMenu') {
            if ((angular.isFunction(this.task.isSheet) && this.task.isSheet()) || this.taskUUID !== this.$routeParams.taskUUID) {
                path = btoa("/sheet/" + this.$routeParams.taskUUID);
            } else {
                path = btoa("/overview/" + this.taskUUID);
            }
            path = '/invigilatorMenu/' + this.taskUUID + '/' + path;
            this.$rootScope.iaaPreUnlocked = true;
        } else {
            if ((angular.isFunction(this.task.isSheet) && this.task.isSheet()) || this.taskUUID !== this.$routeParams.taskUUID) {
                path = "/sheet/" + this.$routeParams.taskUUID;
            } else {
                path = "/overview/" + this.taskUUID;
            }
        }
        this.$location.path(path);
        this.redirect = null;
    }
}

export default function registerPauseController(assessmentFrontendControllers) {
    assessmentFrontendControllers.controller('PauseController', ['$scope', '$rootScope', '$location', '$routeParams', 'TaskSelectionService', 'TaskDataService', '$bridges', '$signals', '$translate', 'TaskCountdownService', '$interval', '$timeout', PauseController]);
};